import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, IconButton } from '@/components/ui/button.tsx';
import { useProfile } from '@hooks/useProfile';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  Bug,
  ChevronRight,
  LifeBuoy,
  LogOut,
  ScrollText,
  SlidersHorizontal,
  UserCog,
  UserIcon,
} from 'lucide-react';
import { useLogout } from '@/auth/hooks/useAuth.ts';
import { useAccessToken } from '@/auth/hooks/useAccessToken';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useReportProblemStore } from '@modules/support/reportProblemStore.ts';
import type { LucideIcon } from 'lucide-react';
import type { ButtonProps } from '@/components/ui/button.tsx';

interface MenuItem {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: ButtonProps['variant'];
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: -12, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

const DROPDOWN_WIDTH = 288;

export default function UserDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = useProfile();
  const { mutate } = useLogout();
  const { clearToken } = useAccessToken();

  const updateTriggerRect = useCallback(() => {
    const button = containerRef.current?.querySelector('button');
    if (button) {
      setTriggerRect(button.getBoundingClientRect());
    }
  }, []);

  const closeDropdown = () => setIsOpen(false);
  const toggleDropdown = () =>
    setIsOpen((prev) => {
      const next = !prev;
      if (!prev) {
        updateTriggerRect();
      }
      return next;
    });

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    updateTriggerRect();

    const handleResize = () => updateTriggerRect();
    const handleScroll = () => updateTriggerRect();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateTriggerRect]);

  const openReportProblem = useReportProblemStore((state) => state.open);

  const handleLogout = () => {
    try {
      mutate();
      clearToken();
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  const sections: MenuSection[] = [
    {
      title: 'Workspace',
      items: [
        {
          label: 'General Settings',
          icon: SlidersHorizontal,
          onClick: () => navigate('/settings/general'),
        },
        {
          label: 'Account Settings',
          icon: UserCog,
          onClick: () => {},
        },
      ],
    },
    {
      title: 'Support',
      items: [
        {
          label: 'Help Center',
          icon: LifeBuoy,
          onClick: () => {},
        },
        {
          label: 'Terms of Usage',
          icon: ScrollText,
          onClick: () => navigate('/settings/terms'),
        },
        {
          label: 'Report a bug',
          icon: Bug,
          onClick: () => openReportProblem(),
        },
      ],
    },
    {
      title: 'Account',
      items: [
        {
          label: 'Logout',
          icon: LogOut,
          onClick: handleLogout,
          variant: 'ghost-danger-glow',
        },
      ],
    },
  ];

  const dropdownPosition = hasMounted && triggerRect
    ? (() => {
        const minLeft = 16;
        const maxLeft = Math.max(window.innerWidth - DROPDOWN_WIDTH - 16, minLeft);
        const desiredLeft = triggerRect.right - DROPDOWN_WIDTH;

        return {
          top: triggerRect.bottom + 12,
          left: Math.min(Math.max(desiredLeft, minLeft), maxLeft),
        };
      })()
    : undefined;

  return (
    <div ref={containerRef} className="relative z-10">
      <IconButton
        variant="ghost-light"
        size="icon"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-2xl border border-content-highlight/60 bg-content/80 text-white transition hover:border-primary/50 hover:text-primary',
          isOpen && 'border-primary/60 text-primary',
        )}
      >
        <UserIcon className="h-5 w-5" />
      </IconButton>

      {hasMounted &&
        createPortal(
          <>
            <AnimatePresence>
              {isOpen && (
                <motion.button
                  type="button"
                  className="fixed inset-0 z-[998] cursor-default bg-gradient-to-br from-black/60 via-[#0b1120]/60 to-black/70 backdrop-blur-md"
                  onClick={closeDropdown}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isOpen && dropdownPosition && (
                <motion.div
                  key="user-dropdown"
                  className="fixed z-[9999] w-72 origin-top-right rounded-3xl border border-content-highlight bg-content p-5 text-white shadow-2xl shadow-content-dark/40 backdrop-blur"
                  style={dropdownPosition}
                  initial={{ opacity: 0, y: -12, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  {data ? (
                    <div className="space-y-4">
                      <motion.div
                        className="flex items-center gap-3 rounded-2xl bg-content-dark px-4 py-3"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/20 text-primary">
                          <UserIcon className="h-5 w-5" />
                        </span>
                        <div>
                          <p className="text-sm font-semibold">{data.username}</p>
                          
                        </div>
                      </motion.div>

                      {sections.map((section) => (
                        <motion.div key={section.title} variants={containerVariants} initial="hidden" animate="show" exit="hidden" className="space-y-2">
                          <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">{section.title}</p>

                          {section.items.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <motion.div key={item.label} variants={itemVariants}>
                                <Button
                                  variant={item.variant ?? 'ghost-light'}
                                  size="lg"
                                  type="button"
                                  onClick={() => {
                                    closeDropdown();
                                    item.onClick();
                                  }}
                                  className={cn(
                                    'flex w-full items-center justify-between gap-3 rounded-2xl border border-transparent bg-content px-4 py-3 text-sm font-medium text-white transition hover:border-primary/40',
                                    item.variant === 'ghost-danger-glow' && 'hover:border-transparent hover:shadow-red-500/40',
                                  )}
                                >
                                  <span className="flex items-center gap-3">
                                    <ItemIcon className="h-4 w-4 text-primary" />
                                    {item.label}
                                  </span>
                                  <ChevronRight className="h-4 w-4 text-white/40" />
                                </Button>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 rounded-2xl bg-content px-4 py-3">
                        <Skeleton className="h-10 w-10 rounded-2xl" />
                        <div className="w-full space-y-2">
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-36" />
                        </div>
                      </div>

                      {[...Array(5)].map((_, index) => (
                        <Skeleton key={index} className="h-12 w-full rounded-2xl" />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>,
          document.body,
        )}
    </div>
  );
}
